import { Injectable } from '@angular/core';
import {Game, InningPart} from "../model/broadcast/game";
import {Broadcast, ManualData} from "../model/broadcast/broadcast";
import {Team} from "../model/broadcast/team";
import {Player} from "../model/broadcast/player";
import {League} from "../model/league";
import {DEFAULT_SKIN, getSkinById, SkinDef} from "../model/broadcast/skin";

// ODM identity pole (jméno hráče / kód týmu) – černé pole, bílý text (viz skins.scss).
// Statistiky/data naopak běží na bílých panelech (černý text) přes --component-bgr.
const ODM_FIELD_BGR = '#000000';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  constructor() { }

  public getName(player: Player, broadcast: Broadcast): string {
    if (player) {
      // Prefer the (possibly edited) name from the roster, matched by guid.
      // Skip the lookup for entries without a guid (manually added people) so
      // they don't accidentally match each other.
      if (player.guid && broadcast?.fullRosters) {
        const find = broadcast.fullRosters.find(p => p.guid === player.guid);
        if (find?.name) {
          return find.name;
        }
      }
      return player.name ?? '';
    }
    return '';
  }

  public getNumber(player: Player, broadcast: Broadcast): string {
    if (player) {
      // First check if the player has a number (which could be overridden)
      if (player.number !== undefined && player.number !== null) {
        return '#' + player.number;
      }
      // Fall back to fullRosters lookup if player doesn't have a number
      if (player.guid && broadcast?.fullRosters) {
        const find = broadcast.fullRosters.find(p => p.guid === player.guid);
        if (find?.number) {
          return '#' + find.number;
        }
      }
    }
    return ''
  }

  public getTeamBgr(player: Player, broadcast: Broadcast): string {
    if (player && broadcast) {
      return this.getTeamBgrByGuid(player?.teamGuid, broadcast);
    }
    return '#333333';
  }

  public getTeamBgrByGuid(teamGuid: string, broadcast: Broadcast): string {
    // ODM: pole se jmény/score nepoužívají barvu týmu.
    if (broadcast?.config?.skinId === 'ODM') {
      return ODM_FIELD_BGR;
    }
    if (teamGuid && broadcast) {
      if (teamGuid == broadcast.config.homeConfig.id) {
        return broadcast.config.homeConfig.primaryColor;
      } else if (teamGuid == broadcast.config.awayConfig.id) {
        return broadcast.config.awayConfig.primaryColor;
      }
    }
    return '#333333';
  }

  // True, pokud aktivní skin (z config.skinId) nepoužívá loga týmů (ODM norma).
  public hideTeamLogos(broadcast: Broadcast): boolean {
    return !!getSkinById(broadcast?.config?.skinId)?.hideTeamLogos;
  }

  // Strana týmu (home/away) podle config – hook pro per-tým gradient v ODM skinu.
  public getTeamSide(teamGuid: string, broadcast: Broadcast): 'home' | 'away' | '' {
    if (!teamGuid || !broadcast?.config) {
      return '';
    }
    if (teamGuid == broadcast.config.homeConfig?.id) {
      return 'home';
    }
    if (teamGuid == broadcast.config.awayConfig?.id) {
      return 'away';
    }
    return '';
  }

  // 3písmenné označení (kraje) pro daný tým – náhrada za logo v ODM grafice.
  public getTeamShortCode(teamGuid: string, broadcast: Broadcast): string {
    if (!teamGuid || !broadcast?.config) {
      return '';
    }
    if (teamGuid == broadcast.config.homeConfig?.id) {
      return broadcast.config.homeConfig?.shortName || '';
    }
    if (teamGuid == broadcast.config.awayConfig?.id) {
      return broadcast.config.awayConfig?.shortName || '';
    }
    return '';
  }

  getBgrWithOverlay(player: Player, broadcast: Broadcast): string {
    // ODM: ve vysílací grafice se loga týmů nepoužívají.
    if (this.hideTeamLogos(broadcast)) {
      return '';
    }
    if (player && broadcast) {
      if (player.teamGuid == broadcast.config.homeConfig.id) {
        return 'background-image:url(' + broadcast.config.homeConfig.logoUrl + ');';
      } else if (player.teamGuid == broadcast.config.awayConfig.id) {
        return 'background-image:url(' + broadcast.config.awayConfig.logoUrl + ');';
      } else {
        return 'background-image:url(' + broadcast.config.smallLogoUrl + ');';
      }
    }

    return '';
  }

  public positionToString(position: number): string {
    if (position === -1) {
      return 'DP';
    }
    const positions: string[] = [null, 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH'];
    return positions[position];
  }

  public positionToLabel(position: number): string {
    if (position > 0 && position <= 9) {
      return position + ' - ' + this.positionToString(position);
    } else {
      return this.positionToString(position);
    }
  }

  public inningToString(broadcast: Broadcast): string {
    if (!broadcast) return '';
    let data: ManualData;
    if (broadcast?.config?.manualStatus) {
      data = broadcast.manualData;
    } else {
      //todo rozmyslet a dodelat
      //data = this.parseToManualData(broadcast.game);
      return "";
    }
    if (!data) {
      return "";
    }
    if (data.inningPart === 'F') {
      return 'FINAL';
    }
    let inningText;
    let inningNumber;
    let inningPart: InningPart;
    inningPart = data?.inningPart;
    if (inningPart === 'E') {
      inningNumber = data.inning;
      inningText = "END OF ";
    } else if (inningPart === 'M') {
      inningNumber = data.inning;
      inningText = "MIDDLE OF ";
    } else if (inningPart === 'T') {
      inningNumber = data.inning;
      inningText = "TOP OF ";
    } else if (inningPart === 'B') {
      inningNumber = data.inning;
      inningText = "BOTTOM OF ";
    }
    inningText += inningNumber;

    if (inningNumber == 1) {
      inningText += " ST";
    } else if (inningNumber == 2) {
      inningText += " ND";
    } else if (inningNumber == 3) {
      inningText += " RD";
    } else {
      inningText += " TH";
    }
    return inningText;
  }

  // Inning indikátor pro box-score. ODM ho v hlavičce nezobrazuje (prázdný text),
  // ostatní skiny dostávají plný text (inningToString) – zpětná kompatibilita.
  public inningToShort(broadcast: Broadcast): string {
    return broadcast?.config?.skinId === 'ODM' ? '' : this.inningToString(broadcast);
  }

  // Eufonické "z" vs "ze" před číslem v genitivu (zápis "X z/ze Y", např. 2 ze 4).
  // "ze" se používá před čísly, jejichž genitiv začíná těžkou souhláskovou skupinou
  // nebo s/z/š/ž (dvou, tří, čtyř, šesti, sedmi … a jejich „náctky").
  public czOutOf(n: number): string {
    const ze = new Set([2, 3, 4, 6, 7, 12, 13, 14, 16, 17]);
    return ze.has(n) ? 'ze' : 'z';
  }

  public isLicencedLeague(url: string): boolean {
    return this.isCzechExl(url) || this.isEC23(url);
  }

  public isLicencedForBroadcast(league: League, homeTeamId: string): boolean {
    if (!league) return false;
    if (league.licenced) return true;
    return !!league.teams?.find(t => t.id === homeTeamId)?.licenced;
  }

  // Vrátí definici skinu. Priorita: explicitní skinId (z ligy), pak legacy detekce
  // podle URL (zpětná kompatibilita), nakonec výchozí skin.
  public getSkinDef(skinId?: string, url?: string): SkinDef {
    const byId = getSkinById(skinId);
    if (byId) {
      return byId;
    }
    if (this.isEC23(url)) {
      return getSkinById('EC23');
    }
    return DEFAULT_SKIN;
  }

  public getSkin(url?: string): string {
    return this.getSkinDef(undefined, url).id;
  }

  private isCzechExl(url: string): boolean {
    return url?.startsWith('https://stats.baseball.cz/cs/events/2024-extraliga') ||
      url?.startsWith('https://stats.baseball.cz/cs/events/2023-sandbox');
  }

  private isEC23(url: string): boolean {
    return url?.startsWith('https://www.wbsceurope.org/en/events') || url?.startsWith('https://stats.baseball.cz/cs/events/2023-sandbox/schedule-and-results');
  }

  public getTeamGuid(shortName: string) {
    const teamGuids = {
      AUT: '23893',
      CZE: '23896',
      GRE: '23892',
      ESP: '23903',
      GBR: '23904',
      HUN: '23902',
      ITA: '23889',
      SWE: '23901',
      CRO: '23894',
      FRA: '23898',
      NED: '23897',
      UKR: '23899',
      BEL: '23895',
      GER: '23900',
      ISR: '23891',
      SUI: '23890',
      ARR: '31091',
      DRA: '31098',
      EAG: '31096',
      HLU: '31092',
      HRO: '31095',
      KOT: '31093',
      NUC: '31097',
      TEM: '31094',
    };
    return teamGuids[shortName] ? teamGuids[shortName] : null;
  }

  private getTeamFullName(shortName: string) {
    const translateToFullName = {
      TEM: 'Tempo Praha',
      DRA: 'Draci Brno',
      NUC: 'Třebíč Nuclears',
      ARR: 'Arrows Ostrava',
      EAG: 'Eagles Praha',
      TEC: 'Technika Brno',
      HLU: 'Sokol Hluboká',
      HRO: 'Hroši Brno',
      KOT: 'Kotlářka Praha',
      GLO: 'BBJO Globetrotters',
      NSW: 'New South Wales Country',
      AUT: 'Austria',
      CZE: 'Czechia',
      GRE: 'Greece',
      ESP: 'Spain',
      GBR: 'Great Britain',
      HUN: 'Hungary',
      ITA: 'Italy',
      SWE: 'Sweden',
      CRO: 'Croatia',
      FRA: 'France',
      NED: 'Netherlands',
      UKR: 'Ukraine',
      BEL: 'Belgium',
      GER: 'Germany',
      ISR: 'Israel',
      SUI: 'Switzerland',
    }
    return translateToFullName[shortName] ? translateToFullName[shortName] : shortName;
  }

  private getDefaultPrimaryColor(shortName: string) {
    const colors = {
      TEM: '#172743',
      DRA: '#020202',
      NUC: '#000055',
      ARR: '#0F1A2C',
      EAG: '#004A39',
      TEC: '#DA2128',
      HLU: '#0032A0',
      HRO: '#041C2C',
      KOT: '#0032A0',
      GLO: '#19315B',
      NSW: '#006',
      AUT: '#DF0000',
      CZE: '#00006F', //#11457E
      GRE: '#1262AB',
      ESP: '#c60b1e',
      GBR: '#000F63',
      HUN: '#388d00',
      ITA: '#005700',
      SWE: '#0E6CA4',
      CRO: '#171796',
      FRA: '#00267f',
      NED: '#21468b',
      UKR: '#005bbb',
      BEL: '#EE2B37',
      GER: '#020202',
      ISR: '#00c',
      SUI: '#d52b1e',
    };
    return colors[shortName] ? colors[shortName] : colors['TEM'];
  }

  private getDefaultSecondaryColor(shortName: string) {
    const colors = {
      TEM: '#D6252B',
      KOT: '#D6252B',
      DRA: '#BA9836',
      NUC: '#FFFFFF',
      ARR: '#D6252B',
      EAG: '#FFFFFF',
      TEC: '#002A68',
      HLU: '#002855',
      HRO: '#5F5F5F',
      AUT: '#FFFFFF',
      CZE: '#FFFFFF',
      GLO: '#B94247',
      NSW: '#C00'
    };
    return colors[shortName] ? colors[shortName] : colors['TEM'];
  }

  public getLogoUrl(shortName: string, broadcastUrl?: string): string {
    const skin = this.getSkin(broadcastUrl);
    if(skin === 'EC23') return '../../../assets/logos/ec23/' + shortName.toLowerCase() + '.svg';
    if(skin === 'default') return '../../../assets/logos/czech/' + shortName.toUpperCase() + '.png';
  }

  public parseToManualData(game: Game): ManualData {
    try {
      return {
        away: game.away.runs,
        awayPitches: this.getPitchCount(game.away),
        balls: game.balls,
        first: !!game.first,
        home: game.home.runs,
        homePitches: this.getPitchCount(game.home),
        inning: game.inning,
        inningPart: game.inningPart,
        outs: game.outs,
        second: !!game.second,
        selectedPerson: null,
        strikes: game.strikes,
        third: !!game.third,
        index: 1,
      };
    } catch (e) {
      console.log("broadcast is null");
      return null;
    }
  }

  public getPitchCount(team: Team): number {
    try {
      return team.pitchers[team.pitchers.length - 1].gameStats.pitching.pitches;
    } catch (e) {
      return 0;
    }
  }

  public formatAvg(value: number | null | undefined): string {
    if (value == null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    if (num >= 1) return num.toFixed(3);
    if (num <= 0) return '.000';
    return '.' + Math.round(num * 1000).toString().padStart(3, '0');
  }
}
